import React, { useState } from 'react';
import { Plus, Trash2, Upload, X, Pencil, Eye, EyeOff, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { mediaUrl } from '../hooks/useProjects';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REGIONS = ['Bali (Indonesia)', 'Northern Cyprus', 'Vietnam', 'Maldives'];
const TYPES = ['Apartment', 'Villa', 'Studio', 'Mixed'];

const EMPTY = {
  title: '', region: REGIONS[0], address: '', type: 'Apartment', price: '',
  apartment_area: '', villa_area: '', description: '', images: [], featured: false, status: 'draft'
};

const toForm = (project) => ({
  title: project.title || '', region: project.region || REGIONS[0], address: project.address || '',
  type: project.type || 'Apartment', price: project.price || '',
  apartment_area: project.apartment_area || '', villa_area: project.villa_area || '',
  description: project.description || '', images: project.images || [],
  featured: !!project.featured, status: project.status || 'draft'
});

export const ProjectsPanel = ({ projects, token, onChanged, toast, formatError }) => {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append('file', file);
        const { data } = await axios.post(`${BACKEND_URL}/api/admin/uploads`, body, { headers: authHeaders });
        setForm((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/projects/${editingId}`, form, { headers: authHeaders });
        toast({ title: 'Project updated', description: form.title });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/projects`, form, { headers: authHeaders });
        toast({ title: 'Project created', description: `${form.title} saved as ${form.status}` });
      }
      reset();
      onChanged();
    } catch (err) {
      toast({ title: 'Could not save project', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (project) => {
    if (!window.confirm(`Delete "${project.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/projects/${project.id}`, { headers: authHeaders });
      if (editingId === project.id) reset();
      toast({ title: 'Project deleted' });
      onChanged();
    } catch (err) {
      toast({ title: 'Could not delete', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  const toggleStatus = async (project) => {
    const next = project.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${BACKEND_URL}/api/admin/projects/${project.id}`, { ...toForm(project), status: next }, { headers: authHeaders });
      toast({ title: next === 'published' ? 'Project published' : 'Moved to draft', description: project.title });
      onChanged();
    } catch (err) {
      toast({ title: 'Could not update', description: formatError(err.response?.data?.detail), variant: 'destructive' });
    }
  };

  const field = (key, label, extra = {}) => (
    <div>
      <Label htmlFor={`project-${key}`} className="text-[#102131]">{label}</Label>
      <Input
        id={`project-${key}`}
        data-testid={`project-${key}-input`}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-2 border-gray-300 focus:border-[#CDA259]"
        {...extra}
      />
    </div>
  );

  return (
    <div className="grid xl:grid-cols-5 gap-6" data-testid="projects-panel">
      <form onSubmit={save} data-testid="project-form" className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-6 space-y-5 h-fit">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#102131]">{editingId ? 'Edit project' : 'Add a project'}</h3>
          {editingId && (
            <button type="button" data-testid="project-cancel-edit" onClick={reset} className="text-xs text-gray-500 hover:text-[#102131]">
              Cancel
            </button>
          )}
        </div>

        {field('title', 'Title', { required: true, placeholder: 'Bali, Uluwatu' })}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="project-region" className="text-[#102131]">Region</Label>
            <select
              id="project-region"
              data-testid="project-region-select"
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="project-type" className="text-[#102131]">Type</Label>
            <select
              id="project-type"
              data-testid="project-type-select"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </div>
        </div>

        {field('address', 'Address', { placeholder: 'Jl. Pantai Pandawa, Bali' })}
        {field('price', 'Price', { placeholder: 'From $88,000' })}

        <div className="grid sm:grid-cols-2 gap-4">
          {field('apartment_area', 'Apartment area', { placeholder: 'From 45 m²' })}
          {field('villa_area', 'Villa area', { placeholder: 'From 120 m²' })}
        </div>

        <div>
          <Label htmlFor="project-description" className="text-[#102131]">Description (English)</Label>
          <Textarea
            id="project-description"
            data-testid="project-description-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-2 border-gray-300 focus:border-[#CDA259] min-h-28"
            placeholder="Write it once in English - it is translated into Lithuanian, Russian and Polish automatically."
          />
          <p className="mt-2 text-xs text-gray-500">Translated automatically on save (LT / RU / PL).</p>
        </div>

        <div>
          <Label className="text-[#102131]">Images</Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {form.images.map((url) => (
              <div key={url} className="relative w-24 h-20 rounded-md overflow-hidden border border-gray-200">
                <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  data-testid="project-remove-image"
                  onClick={() => setForm({ ...form, images: form.images.filter((i) => i !== url) })}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="w-24 h-20 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 cursor-pointer hover:border-[#CDA259] hover:text-[#CDA259] transition-colors duration-200">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
              <span className="text-[10px] mt-1">Upload</span>
              <input
                type="file"
                accept="image/*"
                multiple
                data-testid="project-image-input"
                className="hidden"
                onChange={(e) => e.target.files?.length && uploadImages(e.target.files)}
              />
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500">First image is used as the cover. JPG, PNG or WEBP, up to 10 MB each.</p>
        </div>

        <div className="flex flex-wrap items-center gap-5 pt-1">
          <label className="flex items-center gap-2 text-sm text-[#102131] cursor-pointer">
            <input
              type="checkbox"
              data-testid="project-featured-checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              className="accent-[#CDA259] w-4 h-4"
            />
            Featured on home page
          </label>
          <label className="flex items-center gap-2 text-sm text-[#102131] cursor-pointer">
            <input
              type="checkbox"
              data-testid="project-published-checkbox"
              checked={form.status === 'published'}
              onChange={(e) => setForm({ ...form, status: e.target.checked ? 'published' : 'draft' })}
              className="accent-[#CDA259] w-4 h-4"
            />
            Published (visible on the site)
          </label>
        </div>

        <Button
          type="submit"
          disabled={saving || uploading}
          data-testid="project-save-btn"
          className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold"
        >
          {saving ? 'Saving & translating...' : editingId ? 'Save changes' : <><Plus size={16} className="mr-2" /> Add project</>}
        </Button>
      </form>

      <div className="xl:col-span-3 bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="projects-table">
        {projects.length === 0 ? (
          <p className="p-10 text-center text-gray-500" data-testid="projects-empty">No projects yet.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-[#102131] text-[#F7FDFF]">
              <tr>
                {['', 'Title', 'Region', 'Price', 'Status', ''].map((h, i) => (
                  <th key={i} className="px-4 py-4 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-gray-100 hover:bg-[#F7FDFF] transition-colors duration-150">
                  <td className="px-4 py-3">
                    {p.images?.[0] ? (
                      <img src={mediaUrl(p.images[0])} alt="" className="w-14 h-11 object-cover rounded" />
                    ) : (
                      <div className="w-14 h-11 rounded bg-gray-100" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-[#102131]">
                    <span className="flex items-center gap-2">
                      {p.title}
                      {p.featured && <Star size={13} className="text-[#CDA259] fill-[#CDA259]" />}
                    </span>
                    <span className="block text-xs text-gray-500">{p.type}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.region}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.price}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`project-status-${p.id}`}
                      onClick={() => toggleStatus(p)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-3 py-1 transition-colors duration-150 ${
                        p.status === 'published'
                          ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                          : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {p.status === 'published' ? <Eye size={12} /> : <EyeOff size={12} />}
                      {p.status === 'published' ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 justify-end">
                      <button
                        data-testid={`project-edit-${p.id}`}
                        onClick={() => { setForm(toForm(p)); setEditingId(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="text-gray-400 hover:text-[#CDA259] transition-colors duration-150"
                        aria-label="Edit project"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        data-testid={`project-delete-${p.id}`}
                        onClick={() => remove(p)}
                        className="text-gray-400 hover:text-red-600 transition-colors duration-150"
                        aria-label="Delete project"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectsPanel;
